import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../lib/utils';
import { CreditCard, Calendar, CheckCircle, AlertCircle, Plus, Building, ExternalLink, RefreshCw, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function Payments() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [portfolioROI, setPortfolioROI] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetupDD, setShowSetupDD] = useState(false);
  const [ddSetupUrl, setDdSetupUrl] = useState(null);
  const isTenant = user?.role === 'tenant';
  const isLandlord = user?.role === 'landlord' || user?.role === 'landlord_admin';

  useEffect(() => {
    loadPaymentData();
  }, []);

  const loadPaymentData = async () => {
    try {
      const [invoicesRes, paymentsRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/payments')
      ]);
      setInvoices(invoicesRes.data || []);
      setPayments(paymentsRes.data || []);

      if (isLandlord) {
        try {
          const roiRes = await api.get('/financial/portfolio-roi');
          setPortfolioROI(roiRes.data);
        } catch (e) {
          console.log('ROI not available');
        }
      }
    } catch (error) {
      console.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const setupDirectDebit = async () => {
    try {
      const { data } = await api.post('/payments/gocardless/setup-mandate');
      if (data.authorisation_url) {
        setDdSetupUrl(data.authorisation_url);
        setShowSetupDD(true);
      } else {
        toast.error('Failed to get setup URL');
      }
    } catch (error) {
      toast.error('Failed to setup Direct Debit');
    }
  };

  const generateInvoices = async () => {
    try {
      const { data } = await api.post('/financial/generate-invoices');
      toast.success(`Generated ${data.invoices_created} invoices`);
      loadPaymentData();
    } catch (error) {
      toast.error('Failed to generate invoices');
    }
  };

  const pendingInvoices = invoices.filter(i => !i.paid);
  const totalDue = pendingInvoices.reduce((sum, i) => sum + (i.amount || 0), 0);

  return (
    <DashboardLayout>
      <div className="max-w-7xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-display font-bold text-slate-900 mb-2">Payments</h1>
            <p className="text-lg text-slate-600">
              {isTenant ? 'Manage your rent payments' : 'Track rent collection and payouts'}
            </p>
          </div>
          {isLandlord && (
            <Button onClick={generateInvoices}>
              <RefreshCw className="w-5 h-5 mr-2" />
              Generate Invoices
            </Button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-900">{formatCurrency(totalDue)}</h3>
            <p className="text-sm text-slate-600">Total Due</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-900">{payments.filter(p => p.status === 'confirmed').length}</h3>
            <p className="text-sm text-slate-600">Confirmed Payments</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Calendar className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-900">{pendingInvoices.length}</h3>
            <p className="text-sm text-slate-600">Pending Invoices</p>
          </Card>

          {isLandlord && portfolioROI && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-slate-900">{portfolioROI.gross_yield_percentage}%</h3>
              <p className="text-sm text-slate-600">Portfolio Yield</p>
            </Card>
          )}
        </div>

        {/* Tenant: Setup Direct Debit */}
        {isTenant && (
          <Card className="p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4">Payment Method</h3>
            <div className="flex items-center gap-4">
              <div className="p-4 bg-slate-100 rounded-lg">
                <Building className="w-8 h-8 text-slate-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Direct Debit (GoCardless)</p>
                <p className="text-sm text-slate-600">Automatic monthly payments - never miss rent</p>
              </div>
              <Button onClick={setupDirectDebit}>
                <Plus className="w-4 h-4 mr-2" />
                Setup Direct Debit
              </Button>
            </div>
          </Card>
        )}

        {/* Landlord: Portfolio Summary */}
        {isLandlord && portfolioROI && (
          <Card className="p-6 mb-8 bg-gradient-to-r from-primary to-secondary text-white">
            <h3 className="text-xl font-semibold mb-4">Portfolio Summary</h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <p className="text-white/80 text-sm">Monthly Income</p>
                <p className="text-2xl font-bold">{formatCurrency(portfolioROI.monthly_rental_income || 0)}</p>
              </div>
              <div>
                <p className="text-white/80 text-sm">Annual Income</p>
                <p className="text-2xl font-bold">{formatCurrency(portfolioROI.annual_rental_income || 0)}</p>
              </div>
              <div>
                <p className="text-white/80 text-sm">Occupancy Rate</p>
                <p className="text-2xl font-bold">{portfolioROI.occupancy_rate || 0}%</p>
              </div>
              <div>
                <p className="text-white/80 text-sm">Net Yield</p>
                <p className="text-2xl font-bold">{portfolioROI.net_yield_percentage || 0}%</p>
              </div>
            </div>
          </Card>
        )}

        {/* Invoices */}
        <Card className="p-6 mb-8">
          <h3 className="text-xl font-semibold mb-6">Invoices</h3>
          {invoices.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No invoices yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {invoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    {invoice.paid ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                    )}
                    <div>
                      <p className="font-medium">Rent - {invoice.month}/{invoice.year}</p>
                      <p className="text-sm text-slate-600">Due: {formatDate(invoice.due_date)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{formatCurrency(invoice.amount)}</p>
                    <p className={`text-sm ${invoice.paid ? 'text-green-600' : 'text-yellow-600'}`}>
                      {invoice.paid ? 'Paid' : 'Pending'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Payment History */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold mb-6">Payment History</h3>
          {payments.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No payment history yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {payments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium">Payment via {payment.payment_method || 'Direct Debit'}</p>
                      <p className="text-sm text-slate-600">{formatDate(payment.created_at)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{formatCurrency(payment.amount)}</p>
                    <p className={`text-sm ${payment.status === 'confirmed' ? 'text-green-600' : 'text-yellow-600'}`}>
                      {payment.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Direct Debit Setup Dialog */}
        <Dialog open={showSetupDD} onOpenChange={setShowSetupDD}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Setup Direct Debit</DialogTitle>
            </DialogHeader>
            {ddSetupUrl ? (
              <div className="text-center py-8">
                <Building className="w-16 h-16 mx-auto mb-4 text-primary" />
                <p className="mb-6">Click below to securely setup your Direct Debit with GoCardless</p>
                <Button asChild>
                  <a href={ddSetupUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Setup Direct Debit
                  </a>
                </Button>
              </div>
            ) : (
              <div className="text-center py-8">
                <p>Loading setup link...</p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
